/* eslint-disable no-unused-vars */
import { Delete, Edit } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import accountService from '../services/account-service';
import userRoleService from '../services/user-role-service';

export default function UserRoleManagementPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    usertype: '',
    is_active: true
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 10
  });

  const fetchUsers = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await userRoleService.getUsers(page, limit);
        if (response.success) {
          setUsers(response.data);
          setPagination({
            ...response.pagination,
            limit,
            totalPages: response.pagination.totalPages || 1,
            total: response.total || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit]
  );

  const fetchRoles = useCallback(async () => {
    try {
      const response = await userRoleService.getAllRoles();
      if (response.success) setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        username: user.username,
        password: '',
        usertype: user.role.id,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        usertype: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        await accountService.updateUserById(editingUser.id, formData);
      } else {
        await accountService.createUser(formData);
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
    }
  };

  return (
    <div style={{ marginTop: 40, padding: 20 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        User & Role Management
      </Typography>

      <Button
        variant="contained"
        sx={{ mb: 2 }}
        onClick={() => handleOpenDialog()}
      >
        Add New User
      </Button>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <Paper sx={{ overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>
                    {u.first_name} {u.last_name}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.role.name}</TableCell>
                  <TableCell>{u.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(u)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={async () => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete user ${u.first_name} ${u.last_name}?`
                          )
                        ) {
                          try {
                            await accountService.deleteUserById(u.id);
                            fetchUsers();
                          } catch (error) {
                            console.error('Failed to delete user:', error);
                          }
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <PaginatedControls
        pagination={pagination}
        setPagination={setPagination}
        packagesLength={users.length}
        searchValue=""
        filters={[]}
        handleSearch={fetchUsers}
        fetchAllPackages={fetchUsers}
      />

      {/* ADD / EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            margin="dense"
            value={formData.first_name}
            onChange={(e) =>
              setFormData({ ...formData, first_name: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            margin="dense"
            value={formData.last_name}
            onChange={(e) =>
              setFormData({ ...formData, last_name: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Email"
            margin="dense"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Username"
            margin="dense"
            value={formData.username || ''}
            onChange={(e) =>
              setFormData({ ...formData, username: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            margin="dense"
            value={formData.password || ''}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            helperText={
              editingUser ? 'Leave blank to keep current password' : ''
            }
          />
          <Checkbox
            checked={formData.is_active}
            onChange={(e) =>
              setFormData({ ...formData, is_active: e.target.checked })
            }
          />
          <Typography variant="body2" sx={{ display: 'inline', ml: 1 }}>
            Is Active
          </Typography>
          <Select
            fullWidth
            margin="dense"
            value={formData.usertype}
            onChange={(e) =>
              setFormData({ ...formData, usertype: e.target.value })
            }
            displayEmpty
            sx={{ mt: 2 }}
          >
            <MenuItem value="">Select Role</MenuItem>
            {roles.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
